<template>
  <div class="patient-intro-welcome">
    <div class="patient-intro-welcome__photo">
      <img src="@/assets/images/ai-assistant/patient-mode-woman-eyer.jpg" alt=""/>
    </div>

    <div class="patient-intro-welcome__content">
      <div class="patient-intro-welcome__center">
        <div class="patient-intro-welcome__scroll scrollbar">
          <h1 class="patient-intro-welcome__greeting">{{ $t('aiAssistant.patientIntro.greeting', { name: patientName }) }}</h1>
          <p class="patient-intro-welcome__lede">{{ $t('aiAssistant.patientIntro.lede') }}</p>

          <div class="patient-intro-welcome__card">
            <h2 class="patient-intro-welcome__card-title">{{ $t('aiAssistant.patientIntro.photoCardTitle') }}</h2>
            <p class="patient-intro-welcome__card-text">{{ $t('aiAssistant.patientIntro.photoCardText') }}</p>

            <div class="patient-intro-welcome__grid">
              <div class="patient-intro-welcome__grid-item patient-intro-welcome__grid-item--positive">
                <h3>{{ $t('aiAssistant.patientIntro.aiDoesTitle') }}</h3>
                <p>{{ $t('aiAssistant.patientIntro.aiDoesText') }}</p>
              </div>
              <div class="patient-intro-welcome__grid-item">
                <h3>{{ $t('aiAssistant.patientIntro.aiDoesNotTitle') }}</h3>
                <p>{{ $t('aiAssistant.patientIntro.aiDoesNotText') }}</p>
              </div>
            </div>

            <p class="patient-intro-welcome__anvisa" v-html="$t('aiAssistant.patientIntro.anvisaNote')"></p>
          </div>
        </div>

        <button type="button" class="btn btn-outline-secondary patient-intro-welcome__next" @click="$emit('next')">
          {{ $t('aiAssistant.patientIntro.next') }}
          <IconArrowRight/>
        </button>
      </div>
    </div>

    <div class="patient-intro-welcome__footer">
      <img class="patient-intro-welcome__brand" src="@/assets/images/ai-assistant/phelcom-ai-assistant-inline.png" alt="Phelcom AI Assistant"/>
      <p class="patient-intro-welcome__disclaimer" v-html="$t('aiAssistant.patientIntro.disclaimer')"></p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
// .ai-assistant-tab (the fullscreened ancestor) shows its own dark var(--Shades-BG) behind
// this margin, so the card reads as inset from the browser edges instead of filling them
// edge-to-edge like the rest of the tab does while fullscreened.
.patient-intro-welcome {
  display:                        grid;
  grid-template-columns:          minmax(0, 3fr) minmax(0, 2fr);
  grid-template-rows:             minmax(0, 1fr) auto;
  flex:                           1;
  min-height:                     0;
  padding:                        24px 24px 10px;
  overflow:                       hidden;
  background:                     #fff;
}

.patient-intro-welcome__photo {
  @include                        border_radius(10px);
  display:                        flex;
  align-items:                    center;
  justify-content:                center;
  min-height:                     0;
  padding:                        20px;
  overflow:                       hidden;
  background:                     #000;

  img {
    display:                      block;
    max-width:                    100%;
    max-height:                   100%;
    width:                        auto;
    height:                       auto;
    object-fit:                   contain;
  }
}

.patient-intro-welcome__content {
  display:                        flex;
  flex-direction:                 column;
  min-height:                     0;
  padding:                        40px 48px 24px;
}

// Takes all the space .content has to offer and centers [.scroll, .next] as one block
// within it — .next sits right under .scroll (see its own margin-top) instead of the two
// drifting apart, so the button ends up 20px below the text instead of pinned to the
// bottom of whatever room is left.
.patient-intro-welcome__center {
  display:                        flex;
  flex-direction:                 column;
  justify-content:                center;
  flex:                           1;
  min-height:                     0;
}

.patient-intro-welcome__scroll {
  // grow:0 so this only ever takes the height its content actually needs — letting
  // .center's justify-content:center above center it vertically instead of always
  // stretching to fill (which would leave nothing for centering to do). shrink:1 keeps it
  // scrollable on its own if the content ever doesn't fit the available height.
  flex:                           0 1 auto;
  min-height:                     0;
  overflow-y:                     auto;
  padding-right:                  8px;

  // Narrower than the global .scrollbar class' own default (9px) — matches slide 3's own
  // .ai-assistant-tab__panel scrollbar (ExamAiAssistantTab.scss) for the same 5px everywhere
  // in this patient-mode flow.
  &::-webkit-scrollbar {
    width:                        5px;
  }
}

.patient-intro-welcome__greeting {
  margin:                         0 0 12px;
  font-family:                    'Ubuntu', sans-serif;
  font-size:                      28px;
  font-weight:                    700;
  line-height:                    125%;
  color:                          #101010;
}

.patient-intro-welcome__lede {
  margin:                         0 0 24px;
  font-size:                      14px;
  line-height:                    1.5;
  color:                          #4A4A4A;
}

.patient-intro-welcome__card {
  padding:                        20px;
  border:                         1px solid #E2E2E2;
  border-radius:                  10px;
}

.patient-intro-welcome__card-title {
  margin:                         0 0 8px;
  font-family:                    'Ubuntu', sans-serif;
  font-size:                      16px;
  font-weight:                    700;
  color:                          #101010;
}

.patient-intro-welcome__card-text {
  margin:                         0 0 16px;
  font-size:                      13px;
  line-height:                    1.5;
  color:                          #4A4A4A;
}

.patient-intro-welcome__grid {
  display:                        grid;
  grid-template-columns:          1fr 1fr;
  gap:                            10px;
  margin-bottom:                  16px;
}

.patient-intro-welcome__grid-item {
  padding:                        12px;
  border:                         1.5px solid #E2E2E2;
  border-radius:                  8px;

  &--positive {
    border-color:                 #81BAA7;
    background:                   #E7F2EB;
  }

  h3 {
    margin:                       0 0 4px;
    font-size:                    13px;
    font-weight:                  700;
    color:                        #101010;
  }

  p {
    margin:                       0;
    font-size:                    12px;
    line-height:                  1.4;
    color:                        #4A4A4A;
  }
}

.patient-intro-welcome__anvisa {
  margin:                         0;
  font-size:                      12px;
  line-height:                    1.5;
  color:                          #4A4A4A;

  :deep(strong) {
    color:                        #101010;
  }
}

// .btn-outline-secondary's own color/background/border (see _buttons.scss) follow the
// app-wide theme vars — pinned to fixed literal values here (this card's own colors, same
// idea) so this button can't go dark-on-dark if the doctor happens to be in dark theme.
// :hover/:focus/:active are pinned to the exact same values so nothing visibly changes on
// interaction — this button doesn't invert like a typical outline button does.
.patient-intro-welcome__next {
  color:                          #101010 !important;
  background:                     #FAFAFA !important;
  border-color:                   #D8D8D8 !important;
  font-weight:                    500;
  line-height:                    normal;
  letter-spacing:                 3.12px;
  text-transform:                 uppercase;
  display:                        flex;
  align-items:                    center;
  align-self:                     flex-end;
  gap:                            10px;
  width:                          fit-content;
  margin-top:                     20px;

  &:hover,
  &:focus,
  &:active {
    color:                        #101010 !important;
    background:                   #FAFAFA !important;
    border-color:                 #D8D8D8 !important;
  }

  svg {
    width:                        10px;
    height:                       auto;
  }

  :deep(path) {
    stroke:                       #101010;
  }
}

.patient-intro-welcome__footer {
  grid-column:                    1 / -1;
  display:                        flex;
  align-items:                    center;
  justify-content:                center;
  gap:                            20px;
  padding:                        24px;
}

.patient-intro-welcome__brand {
  height:                         20px;
  width:                          auto;
  flex-shrink:                    0;
}

.patient-intro-welcome__disclaimer {
  color:                          #4A4A4A;
  font-size:                      10px;
  line-height:                    1.4;
  max-width:                      450px;
  margin:                         0;

  :deep(strong) {
    color:                        #101010;
  }
}

@media (max-width: 767px) {
  .patient-intro-welcome {
    grid-template-columns:        minmax(0, 1fr);
    grid-template-rows:           minmax(160px, 30%) minmax(0, 1fr) auto;
    margin:                       12px;
    border-radius:                14px;
    overflow-y:                   auto;
  }

  .patient-intro-welcome__content {
    padding:                      24px 20px 16px;
  }

  .patient-intro-welcome__footer {
    flex-direction:                column;
    align-items:                  flex-start;
    padding:                      16px 20px;
  }

  .patient-intro-welcome__disclaimer {
    text-align:                   left;
  }

  // Side-by-side 1fr/1fr (desktop) leaves each card too narrow to fit its own text on a
  // 320px phone — words got clipped and the whole card scrolled horizontally to see the
  // rest. Stacking them removes the horizontal scroll entirely; each card gets the card's
  // full width to wrap into instead of splitting it in half.
  .patient-intro-welcome__grid {
    grid-template-columns:        minmax(0, 1fr);
  }

  // 28px (desktop) wraps a real patient name across 5-6 lines on a narrow phone, pushing
  // the card/button/footer far down and making the whole screen read as mostly headline.
  // Smaller here only — desktop keeps its own size untouched.
  .patient-intro-welcome__greeting {
    font-size:                    20px;
  }

  .patient-intro-welcome__lede {
    font-size:                    13px;
  }
}
</style>

<script>
export default {
  name: 'ExamAiAssistantPatientIntroWelcome',
  props: {
    patientName: { type: String, default: '' },
  },
  emits: ['next'],
}
</script>
